import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense, useEffect, useRef } from "react";
import { Redirect, Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ThankYouPage, TitleQuotePage } from "./pages/FormPages";
import NotFound from "./pages/NotFound";
import { AboutPage, BlogPage, ContactPage, FaqPage, HomePage, ServicesPage, SiteLayout } from "./pages/SitePages";

const FileOrderHubPage = lazy(() => import("./pages/FileOrderPages").then(module => ({ default: module.FileOrderHubPage })));
const FileOrderFormPage = lazy(() => import("./pages/FileOrderPages").then(module => ({ default: module.FileOrderFormPage })));
const PrivacyPolicyPage = lazy(() => import("./pages/LegalPages").then(module => ({ default: module.PrivacyPolicyPage })));
const TermsOfUsePage = lazy(() => import("./pages/LegalPages").then(module => ({ default: module.TermsOfUsePage })));
const BlogPostPage = lazy(() => import("./pages/BlogPostPages").then(module => ({ default: module.BlogPostPage })));

function RouteLoadingFallback() {
  return <main className="route-loading" aria-busy="true" aria-label="Loading page" />;
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function GoogleAnalyticsRouteTracker() {
  const [location] = useLocation();
  const isInitialRoute = useRef(true);

  useEffect(() => {
    // The tag in index.html records the initial page view. Only record SPA route changes here.
    if (isInitialRoute.current) {
      isInitialRoute.current = false;
      return;
    }

    window.gtag?.("config", "G-G76G96BRG4", {
      page_location: window.location.href,
      page_path: location,
      page_title: document.title,
    });
  }, [location]);

  return null;
}

function AppRoutes() {
  return (
    <SiteLayout>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Switch>
        <Route path="/" component={HomePage} />
        <Route path="/about-us" component={AboutPage} />
        <Route path="/blog" component={BlogPage} />
        <Route path="/blog/owners-vs-lenders-title-insurance-florida"><BlogPostPage slug="owners-vs-lenders-title-insurance-florida" /></Route>
        <Route path="/blog/real-estate-wire-fraud-prevention"><BlogPostPage slug="real-estate-wire-fraud-prevention" /></Route>
        <Route path="/blog/firpta-withholding-florida-property-sale"><BlogPostPage slug="firpta-withholding-florida-property-sale" /></Route>
        <Route path="/faq" component={FaqPage} />
        <Route path="/services" component={ServicesPage} />
        <Route path="/privacy-policy" component={PrivacyPolicyPage} />
        <Route path="/terms-of-use" component={TermsOfUsePage} />
        <Route path="/open-a-file" component={FileOrderHubPage} />
        <Route path="/open-a-file/listing-agent"><FileOrderFormPage variant="listing-agent" /></Route>
        <Route path="/open-a-file/buyers-agent"><FileOrderFormPage variant="buyers-agent" /></Route>
        <Route path="/open-a-file/consumer"><FileOrderFormPage variant="consumer" /></Route>
        <Route path="/open-a-file/others"><FileOrderFormPage variant="lender" /></Route>
        <Route path="/open-a-file/lender"><Redirect to="/open-a-file/others" replace /></Route>
        <Route path="/order-title"><Redirect to="/open-a-file" replace /></Route>
        <Route path="/contact-us" component={ContactPage} />
        <Route path="/title-quote" component={TitleQuotePage} />
        <Route path="/thank-you"><ThankYouPage /></Route>
        <Route path="/title-quote/thank-you"><ThankYouPage quote /></Route>
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </SiteLayout>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <GoogleAnalyticsRouteTracker />
          <AppRoutes />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
